import { createHashRouter, RouterProvider, Outlet, useLocation, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { AuthProvider } from '@/context/AuthContext'
import { trackPageView } from '@/lib/analytics'

// Scroll to top and track page view on route change
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
    trackPageView(pathname)
  }, [pathname])

  return <Outlet />
}

// Page imports
import { Landing } from '@/pages/Landing'
import { Login } from '@/pages/Login'
import { PhysicianFramework } from '@/pages/physician/PhysicianFramework'
import { PhysicianLeaderGuide } from '@/pages/physician/PhysicianLeaderGuide'
import { PhysicianFAQ } from '@/pages/physician/PhysicianFAQ'
import { NonPhysicianFramework } from '@/pages/non-physician/NonPhysicianFramework'
import { NonPhysicianLeaderGuide } from '@/pages/non-physician/NonPhysicianLeaderGuide'
import { NonPhysicianFAQ } from '@/pages/non-physician/NonPhysicianFAQ'
import { EmailTemplates } from '@/pages/admin/EmailTemplates'
import { PimsScoringSensitivity } from '@/pages/admin/PimsScoringSensitivity'
import { EquipDashboardPage } from '@/pages/equip/EquipDashboardPage'
import { EquipLeadershipWrapper } from '@/pages/equip/EquipLeadershipWrapper'
import { EquipAPCDashboardWrapper } from '@/pages/equip/EquipAPCDashboardWrapper'
import { ClinicianFrameworkGateway } from '@/pages/ClinicianFrameworkGateway'
import { UnifiedLeaderGuideGateway } from '@/pages/UnifiedLeaderGuideGateway'
import { UnifiedFAQGateway } from '@/pages/UnifiedFAQGateway'
import { ParticipantLanding } from '@/pages/ParticipantLanding'

// Auth imports
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { PHYSICIAN_TRACK_ROLES, APC_TRACK_ROLES, EQUIP_PERFORMANCE_ROLES, EQUIP_CONTENT_ROLES, EQUIP_ALL, ROLES } from '@/lib/roles'

// EQuIP scope context
import { EquipScopeProvider } from '@/views/equip/context/EquipScopeContext'
import { BreadcrumbProvider } from '@/views/equip/context/BreadcrumbContext'
import { useRBAC } from '@/hooks/use-rbac'

/** Layout wrapper that provides EquipScopeProvider to all EQuIP routes */
function EquipLayout() {
  const { isAdmin, hasAnyRole, hasRole } = useRBAC()
  const isSeniorLead = hasRole(ROLES.EQUIP_SENIOR_LEAD) && !isAdmin
  const showClinicianDropdown = isAdmin || hasAnyRole(ROLES.EQUIP_PHYSICIAN_LEAD, ROLES.EQUIP_APC_LEAD, ROLES.EQUIP_SENIOR_LEAD, ROLES.EQUIP_CSS)

  return (
    <EquipScopeProvider isAdmin={isAdmin} showClinicianDropdown={showClinicianDropdown} isSeniorLead={isSeniorLead}>
      <Outlet />
    </EquipScopeProvider>
  )
}

const router = createHashRouter([
  {
    element: <ScrollToTop />,
    children: [
      // Login is public
      { path: '/login', element: <Login /> },

      // Landing requires authentication (any authenticated user)
      {
        element: <ProtectedRoute />,
        children: [{ path: '/', element: <Landing /> }],
      },

      // EQuIP routes — wrapped with EquipScopeProvider via EquipLayout
      {
        element: <EquipLayout />,
        children: [
          // Performance view - all EQuIP roles (clinician + leadership)
          {
            element: <ProtectedRoute requiredRoles={EQUIP_PERFORMANCE_ROLES} />,
            children: [
              { path: '/performance', element: <EquipDashboardPage /> },
            ],
          },
          // APC dashboard - supervising physician view
          {
            element: <ProtectedRoute requiredRoles={EQUIP_PERFORMANCE_ROLES} />,
            children: [
              { path: '/apc-dashboard', element: <EquipAPCDashboardWrapper /> },
            ],
          },
          // Participant landing - equip.read users
          {
            element: <ProtectedRoute requiredRoles={EQUIP_PERFORMANCE_ROLES} />,
            children: [
              { path: '/user', element: <ParticipantLanding /> },
            ],
          },
          // Clinician framework view - content roles only (no CSS)
          {
            element: <ProtectedRoute requiredRoles={EQUIP_CONTENT_ROLES} />,
            children: [
              { path: '/framework', element: <ClinicianFrameworkGateway /> },
            ],
          },
          // Unified leader guide - leadership + admin only (no equip.read)
          {
            element: <ProtectedRoute requiredRoles={EQUIP_ALL} />,
            children: [
              { path: '/leader-guide', element: <UnifiedLeaderGuideGateway /> },
            ],
          },
          // Unified FAQ - leadership + admin only (no equip.read)
          {
            element: <ProtectedRoute requiredRoles={EQUIP_ALL} />,
            children: [
              { path: '/faq', element: <UnifiedFAQGateway /> },
            ],
          },
          // Leadership view - admin + leadership roles only (no equip.read)
          {
            element: <ProtectedRoute requiredRoles={EQUIP_ALL} />,
            children: [
              { path: '/leadership', element: <EquipLeadershipWrapper /> },
            ],
          },
        ],
      },

      // Physician routes - Admin, equip_admin, equip_physician_lead only
      {
        element: <ProtectedRoute requiredRoles={PHYSICIAN_TRACK_ROLES} />,
        children: [
          { path: '/physician', element: <Navigate to="/physician/framework" replace /> },
          { path: '/physician/framework', element: <PhysicianFramework /> },
          { path: '/physician/leader-guide', element: <PhysicianLeaderGuide /> },
          { path: '/physician/faq', element: <PhysicianFAQ /> },
        ],
      },

      // Non-Physician routes - Admin, equip_admin, equip_apc_lead only
      {
        element: <ProtectedRoute requiredRoles={APC_TRACK_ROLES} />,
        children: [
          { path: '/non-physician', element: <Navigate to="/non-physician/framework" replace /> },
          { path: '/non-physician/framework', element: <NonPhysicianFramework /> },
          { path: '/non-physician/leader-guide', element: <NonPhysicianLeaderGuide /> },
          { path: '/non-physician/faq', element: <NonPhysicianFAQ /> },
        ],
      },

      // Admin routes - Admin role only
      // Note: PIMs sensitivity is intentionally NOT linked from any nav.
      // Reachable by direct URL only: /#/admin/pims-sensitivity
      {
        element: <ProtectedRoute requiredRoles={[ROLES.ADMIN]} />,
        children: [
          { path: '/admin/email-templates', element: <EmailTemplates /> },
          { path: '/admin/pims-sensitivity', element: <PimsScoringSensitivity /> },
        ],
      },
    ],
  },
])

export function Router() {
  return (
    <AuthProvider>
      <BreadcrumbProvider>
        <RouterProvider router={router} />
      </BreadcrumbProvider>
    </AuthProvider>
  )
}
